'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type CSSProperties,
} from 'react';
import { cn } from '../../lib/cn';
import {
  SCROLLBAR_CLASS,
  SCROLLBAR_TRACK_BG_CLASS,
  SCROLLBAR_TRACK_BORDER_CLASS,
} from '../../lib/scrollbar';
import { useInsideModal, usePortalContainer } from '../../lib/portal-container';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { sanitizeInlineHtml } from '../../lib/sanitize-inline-html';
import { SField, type SFieldAddonAlign } from '../SField';
import { SGhostButton } from '../SGhostButton';
import { SIcon, type SIconName } from '../SIcon';
import { SPortal } from '../SPortal';
import { type SColor } from '../../lib/color';
import { withDisplayName } from '../../lib/display-name';
import { type STooltipProps } from '../STooltip';
import {
  collectGroupLeafValues,
  extractText,
  flattenOptions,
  isHtmlLabel,
  selectableOptions,
  type SSelectOption,
  type SSelectType,
} from './select.config';

export type { SSelectOption, SSelectType } from './select.config';

/** 검색바 노출 임계값 — useSearch=true 이고 옵션(리프)이 이 수 이상일 때만 검색바 표시 (원본 SEARCH_THRESHOLD) */
const SEARCH_THRESHOLD = 11;

export interface SSelectHandle {
  /** 트리거 버튼에 포커스 (sdFocus) */
  focus: () => void;
  /** 드롭다운 열기 (sdOpen) */
  open: () => void;
}

export interface SSelectProps {
  /** 선택 값. multi 타입이면 배열. emitValue=false(기본) 라운드트립을 위해 SSelectOption 객체도 허용 */
  value?: (string | number | SSelectOption) | (string | number | SSelectOption)[] | null;
  /** 값 변경 (sdUpdate). emitValue=false(기본)면 SSelectOption(들), true면 원시값 */
  onValueChange?: (value: any) => void;
  /** 열림/닫힘 변경 (sdDropDownShow) */
  onOpenChange?: (open: boolean) => void;
  /** true면 SSelectOption 대신 원시값(string|number) 방출 */
  emitValue?: boolean;
  /** 폼 연동용 name 속성 */
  name?: string;
  /** 옵션 목록 (children으로 계층 표현) */
  options?: SSelectOption[];
  /** 타입: default | multi | default_depth | multi_depth */
  type?: SSelectType;
  /** 검색 입력 사용 */
  useSearch?: boolean;
  /** 전체선택 옵션 사용 (multi 전용) */
  useSelectAll?: boolean;
  /** 전체선택 레이블 */
  allSelectedLabel?: string;
  /** placeholder */
  placeholder?: string;
  /** 비활성 */
  disabled?: boolean;
  /** 에러 상태 */
  error?: boolean;
  /** 유효성 규칙 — 닫힐 때 자동 검증 */
  rules?: Rule[];
  /** 레이블 툴팁 상세 옵션 */
  labelTooltipProps?: Partial<STooltipProps>;
  /** 드롭다운 최대 높이 */
  dropdownHeight?: string;
  /** 드롭다운 너비 (없으면 트리거 너비) */
  dropdownWidth?: string;
  /** 드롭다운 최대 너비 */
  maxDropdownWidth?: string;
  // ── SField 래퍼 props ──
  label?: string;
  labelWidth?: number | string;
  /** 레이블 영역 아이콘 */
  icon?: SIconName;
  iconColor?: SColor;
  /** 레이블 툴팁 텍스트 */
  labelTooltip?: string;
  /** 우측 어드온 레이블 */
  addonLabel?: string;
  /** 어드온 정렬 */
  addonAlign?: SFieldAddonAlign;
  hint?: string;
  errorMessage?: string;
  width?: number | string;
  className?: string;
  style?: CSSProperties;
}

/**
 * SSelect — sd-select 포팅.
 * 단일/다중 선택, 검색(useSearch), 전체선택(useSelectAll), 계층(depth), SField 통합.
 */
const SSelectImpl = forwardRef<SSelectHandle, SSelectProps>(function SSelect(
  {
    value,
    onValueChange,
    onOpenChange,
    emitValue = false,
    name,
    options = [],
    type = 'default',
    useSearch = false,
    useSelectAll = false,
    allSelectedLabel = '전체',
    placeholder = '선택',
    disabled = false,
    error = false,
    rules,
    labelTooltipProps,
    dropdownHeight = '260px',
    dropdownWidth,
    maxDropdownWidth = '640px',
    label,
    labelWidth,
    icon,
    iconColor,
    labelTooltip,
    addonLabel,
    addonAlign,
    hint,
    errorMessage,
    width,
    className,
    style,
  }: SSelectProps,
  ref,
) {
  const multi = type === 'multi' || type === 'multi_depth';
  const portalContainer = usePortalContainer();
  const insideModal = useInsideModal();
  const [search, setSearch] = useState('');
  const [ruleError, setRuleError] = useState('');
  const [open, setOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  /**
   * 리스트는 ref 가 아니라 state 로 잡는다 — 포털 콘텐츠는 open 이 true 가 된 커밋이 아니라
   * 몇 커밋 뒤에 마운트되므로, ref 로 두면 스크롤 effect 가 도는 시점에 아직 null 이고
   * 이후 다시 실행될 계기가 없어 진입 스크롤이 통째로 유실된다.
   * (identity 가 고정된 콜백 ref 여야 리렌더마다 detach/attach 되며 무한 루프가 나지 않는다)
   */
  const [listEl, setListEl] = useState<HTMLDivElement | null>(null);
  const setListRef = useCallback((node: HTMLDivElement | null) => setListEl(node), []);
  // 바깥 클릭으로 닫힌 경우를 표시 — onCloseAutoFocus 에서 포커스 회수 여부를 가른다.
  const dismissedByPointerRef = useRef(false);
  // 열린 직후 1회만 true — 선택 항목을 리스트 맨 위로 끌어올리는 스크롤을 트리거한다.
  // (이후 키보드 탐색은 기존대로 최소 스크롤만 한다)
  const alignSelectedTopRef = useRef(false);
  // 리스트 순서상 첫 선택 항목의 탐색 인덱스. 렌더마다 갱신하되 effect 의존성에는 넣지 않는다
  // — multi 에서 항목을 토글할 때마다 포커스가 되돌아가는 것을 막기 위함.
  const selectedAnchorRef = useRef(-1);

  const flat = useMemo(() => flattenOptions(options), [options]);
  const selectable = useMemo(() => selectableOptions(flat), [flat]);
  // 선택 가능한 리프가 하나라도 있는데 전부 비활성이면 select 자체를 비활성화한다.
  // (옵션이 아예 없는 empty 상태와 구분하기 위해 리프 존재 여부를 함께 확인)
  const hasLeaf = useMemo(() => flat.some(o => !o.isGroup), [flat]);
  const allOptionsDisabled = hasLeaf && selectable.length === 0;
  const effectiveDisabled = disabled || allOptionsDisabled;
  // 원본: useSearch=true 이고 리프 옵션이 SEARCH_THRESHOLD(11) 이상일 때만 검색바 노출
  const showSearch = useSearch && selectable.length >= SEARCH_THRESHOLD;
  const labelByValue = useMemo(() => new Map(flat.map(o => [o.value, o.label])), [flat]);
  const rawByValue = useMemo(() => new Map(flat.map(o => [o.value, o.raw])), [flat]);
  const optionByValue = useMemo(
    () => new Map(flat.map(o => [o.value, { value: o.raw, label: o.label } as SSelectOption])),
    [flat],
  );

  // 내부는 문자열 배열로 정규화. value가 SSelectOption 객체면 .value를 꺼낸다.
  // emitValue=false일 때 방출값이 객체여도 다시 넣었을 때 비교가 깨지지 않게 맞춘다.
  const selected: string[] = useMemo(() => {
    if (value == null) return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr
      .map(v => (v != null && typeof v === 'object' ? (v as SSelectOption).value : v))
      .filter(v => v != null && v !== '')
      .map(String);
  }, [value]);

  const selectedRef = useRef<string[]>(selected);
  selectedRef.current = selected;

  const emit = (next: string[]) => {
    if (!onValueChange) return;
    // emitValue=true → 원시값 / false → SSelectOption 객체 방출
    const mapped = next.map(v =>
      emitValue ? (rawByValue.get(v) ?? v) : (optionByValue.get(v) ?? { value: v, label: v }),
    );
    onValueChange(multi ? mapped : (mapped[0] ?? (emitValue ? '' : null)));
  };

  /**
   * `valueForRules` — 선택 직후 같은 틱에 닫는 경로(단일 선택)에서 방금 고른 값을 넘긴다.
   * `selectedRef.current` 는 렌더 중에만 갱신되므로, emit() 직후에는 아직 이전 값이라
   * 그대로 검증하면 첫 선택에서 required 가 오탐하고 이후로도 한 박자씩 밀린다.
   */
  const commitOpen = (next: boolean, valueForRules?: string[]) => {
    setOpen(next);
    onOpenChange?.(next);

    if (next) {
      dismissedByPointerRef.current = false;
      alignSelectedTopRef.current = true;
    } else {
      // 닫힐 때 transient UI 상태(검색/대기열림)를 함께 정리해 다음 오픈이 깨끗하게 시작되게 한다.
      setPendingOpen(false);
      setSearch('');
      if (rules && rules.length > 0) {
        const current = valueForRules ?? selectedRef.current;
        setRuleError(runRules(multi ? current : (current[0] ?? ''), rules));
      }
    }
  };

  const requestOpen = (next: boolean) => {
    if (next && insideModal && portalContainer == null) {
      // 모달 내부 첫 프레임에는 portal target ref 가 아직 없을 수 있다.
      // 이 순간 body로 열어버리면 포커스/dismiss 타이밍이 흔들릴 수 있어 준비 후 연다.
      setPendingOpen(true);
      return;
    }
    commitOpen(next);
  };

  useEffect(() => {
    if (!insideModal || portalContainer == null || !pendingOpen) return;
    setPendingOpen(false);
    commitOpen(true);
  }, [insideModal, pendingOpen, portalContainer]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => triggerRef.current?.focus(),
      open: () => requestOpen(true),
    }),
    [insideModal, portalContainer],
  );

  // SForm 등록 — submit 시 폼이 이 필드를 일괄 검증한다 (폼 밖이면 no-op)
  useFormField({
    name,
    validate: () => {
      const msg = runRules(multi ? selected : (selected[0] ?? ''), rules);
      setRuleError(msg);
      return msg === '';
    },
    resetValidation: () => setRuleError(''),
  });

  const filtered = useMemo(() => {
    if (!showSearch || search.trim() === '') return flat;
    const q = search.trim().toLowerCase();
    return flat.filter(
      o =>
        o.isGroup ||
        (isHtmlLabel(o.label) ? extractText(o.label) : o.label).toLowerCase().includes(q),
    );
  }, [flat, search, showSearch]);

  const allValues = selectable.map(o => o.value);
  const allSelected = allValues.length > 0 && allValues.every(v => selected.includes(v));
  // 표시할(그룹 아닌) 항목이 하나도 없으면 empty 상태 (options=[] 또는 검색 결과 없음)
  const hasItems = filtered.some(o => !o.isGroup);
  // 그룹(부모) 렌더 인덱스 → 하위(자손) 리프 value 목록. multi_depth 부모 체크박스 계산에 사용.
  const groupLeafValues = useMemo(() => {
    const map = new Map<number, string[]>();
    filtered.forEach((o, i) => {
      if (o.isGroup) map.set(i, collectGroupLeafValues(filtered, i));
    });
    return map;
  }, [filtered]);
  const navigableItems = useMemo(() => {
    const items: Array<{
      key: string;
      isSelectAll?: boolean;
      isGroup?: boolean;
      value?: string;
      leafValues?: string[];
    }> = [];
    if (multi && useSelectAll && allValues.length > 0) {
      items.push({ key: '__select_all__', isSelectAll: true });
    }
    filtered.forEach((o, i) => {
      if (o.disabled) return;
      if (o.isGroup) {
        // multi 계열만 그룹(부모)을 선택 대상에 포함 (하위 리프 전체 토글). default_depth 그룹은 비선택.
        if (!multi) return;
        const leafValues = groupLeafValues.get(i) ?? [];
        if (leafValues.length === 0) return;
        items.push({ key: `__group_${i}__`, isGroup: true, leafValues });
      } else {
        items.push({ key: o.value, value: o.value });
      }
    });
    return items;
  }, [allValues.length, filtered, groupLeafValues, multi, useSelectAll]);

  /**
   * 열 때 기준이 될 선택 항목 — navigableItems 가 화면 순서 그대로라 findIndex 결과가
   * 곧 "리스트 순서상 첫 선택 항목"이다. multi 에서 나중에 고른 값이 앞줄에 있어도
   * 선택 순서가 아닌 화면 순서를 따른다. 선택 항목이 비활성이라 탐색 대상에서 빠졌거나
   * 검색으로 걸러진 경우엔 -1 → 기존대로 첫 항목에 포커스한다.
   */
  const selectedAnchorIndex = useMemo(() => {
    if (selected.length === 0) return -1;
    const selectedSet = new Set(selected);
    return navigableItems.findIndex(item => item.value != null && selectedSet.has(item.value));
  }, [navigableItems, selected]);
  selectedAnchorRef.current = selectedAnchorIndex;

  const triggerText = (() => {
    if (selected.length === 0) return placeholder;
    if (multi) {
      // 원본: 전체 선택 시 allSelectedLabel, 아니면 선택 라벨을 ", "로 join
      if (allSelected) return allSelectedLabel;
      return selected
        .map(v => labelByValue.get(v) ?? v)
        .filter(Boolean)
        .join(', ');
    }
    return labelByValue.get(selected[0]) ?? selected[0];
  })();

  const isPlaceholder = selected.length === 0;

  const handleSelect = (nextValue: string) => {
    if (multi) {
      const next = selected.includes(nextValue)
        ? selected.filter(v => v !== nextValue)
        : [...selected, nextValue];
      emit(next);
      return;
    }

    emit([nextValue]);
    // 방금 고른 값으로 검증한다 — emit() 만으로는 selectedRef 가 아직 갱신되지 않는다.
    commitOpen(false, [nextValue]);
  };

  const handleSelectAll = () => {
    emit(allSelected ? [] : allValues);
  };

  // multi_depth: 그룹(부모) 클릭 → 하위 리프 전체 토글. 전부 선택돼 있으면 해제, 아니면 채운다.
  const handleToggleGroup = (leafValues: string[]) => {
    if (leafValues.length === 0) return;
    const selectedSet = new Set(selected);
    const allSelectedInGroup = leafValues.every(v => selectedSet.has(v));
    if (allSelectedInGroup) {
      const remove = new Set(leafValues);
      emit(selected.filter(v => !remove.has(v)));
    } else {
      emit([...selected, ...leafValues.filter(v => !selectedSet.has(v))]);
    }
  };

  // 열림/검색어 변경 시 포커스 위치 초기화.
  // 열린 직후에는 선택 항목(multi 는 화면 순서상 첫 선택)에, 검색 후에는 첫 항목에 둔다.
  useEffect(() => {
    if (!open || navigableItems.length === 0) {
      setFocusedIndex(-1);
      return;
    }
    const anchor = alignSelectedTopRef.current ? selectedAnchorRef.current : -1;
    setFocusedIndex(anchor >= 0 ? anchor : 0);
  }, [open, search, navigableItems.length]);

  // 페인트 전에 스크롤을 잡아야 리스트가 맨 위에서 한 번 그려졌다가 튀는 게 보이지 않는다.
  useLayoutEffect(() => {
    if (!open || focusedIndex < 0 || !listEl) return;
    const focusedEl = listEl.querySelector('[data-select-focused="true"]') as HTMLElement | null;
    if (!focusedEl) return;

    // scrollIntoView는 상위 스크롤 컨테이너까지 흔들 수 있어 리스트 내부 스크롤만 직접 조정한다.
    const listRect = listEl.getBoundingClientRect();
    const itemRect = focusedEl.getBoundingClientRect();

    // 진입 직후 1회: 최소 스크롤이 아니라 선택 항목을 리스트 맨 위로 올린다.
    // 검색바는 sticky 라 리스트 최상단을 덮으므로 그 높이만큼 내려 가려지지 않게 한다.
    // (끝에 가까운 항목은 스크롤 한계에 걸려 맨 위까지 못 갈 수 있다 — 브라우저가 알아서 클램프)
    if (alignSelectedTopRef.current) {
      alignSelectedTopRef.current = false;
      const stickyOffset = searchBarRef.current?.offsetHeight ?? 0;
      listEl.scrollTop += itemRect.top - listRect.top - stickyOffset;
      return;
    }

    if (itemRect.top < listRect.top) {
      listEl.scrollTop += itemRect.top - listRect.top;
    } else if (itemRect.bottom > listRect.bottom) {
      listEl.scrollTop += itemRect.bottom - listRect.bottom;
    }
  }, [focusedIndex, open, listEl]);

  const moveFocusedIndex = (direction: 'next' | 'prev') => {
    if (!open || navigableItems.length === 0) return;
    setFocusedIndex(current => {
      if (direction === 'next') {
        return current < 0 ? 0 : (current + 1) % navigableItems.length;
      }
      return current <= 0 ? navigableItems.length - 1 : current - 1;
    });
  };

  const selectFocusedItem = () => {
    if (focusedIndex < 0 || focusedIndex >= navigableItems.length) return;
    const focused = navigableItems[focusedIndex];
    // 전체 선택도 일반 옵션과 같은 키보드 탐색 순서에 포함해 Enter 동작을 통일한다.
    if (focused.isSelectAll) {
      handleSelectAll();
      return;
    }
    // multi_depth 그룹(부모): Enter 로 하위 리프 전체 토글
    if (focused.isGroup) {
      handleToggleGroup(focused.leafValues ?? []);
      return;
    }
    handleSelect(focused.value!);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!open) {
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !effectiveDisabled) {
        event.preventDefault();
        requestOpen(true);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveFocusedIndex('next');
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveFocusedIndex('prev');
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      selectFocusedItem();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      commitOpen(false);
    }
  };

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      icon={icon}
      iconColor={iconColor}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      addonLabel={addonLabel}
      addonAlign={addonAlign}
      hint={hint}
      error={error || ruleError !== ''}
      status={rules && rules.length > 0 ? (ruleError !== '' ? 'error' : undefined) : undefined}
      errorMessage={ruleError !== '' ? ruleError : errorMessage}
      disabled={effectiveDisabled}
      width={width}
      className={className}
      style={style}
    >
      {name && <input type="hidden" name={name} value={selected.join(',')} />}

      <button
        ref={triggerRef}
        type="button"
        disabled={effectiveDisabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => requestOpen(!open)}
        onKeyDown={handleKeyDown}
        className="flex h-full w-full cursor-pointer items-center justify-between gap-[8px] bg-transparent px-[12px] text-left text-[12px] leading-[20px] outline-none disabled:cursor-not-allowed"
        style={{
          color: effectiveDisabled
            ? 'var(--cmp-select-text-disabled)'
            : isPlaceholder
              ? 'var(--cmp-select-icon-default)'
              : 'var(--cmp-select-text-default)',
        }}
      >
        {/* HTML 라벨이면 살균 후 렌더 (원본 isHtmlLabel + sanitizeInlineHtml) */}
        {isHtmlLabel(triggerText) ? (
          <span
            className="truncate"
            dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(triggerText) }}
          />
        ) : (
          <span className="truncate">{triggerText}</span>
        )}
        <SIcon
          name="chevronDown"
          size={12}
          color={
            effectiveDisabled ? 'var(--cmp-select-icon-disabled)' : 'var(--cmp-select-icon-default)'
          }
          className={cn('shrink-0 transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      <SPortal
        open={open}
        onClose={() => commitOpen(false)}
        onOpenChange={next => {
          if (next) requestOpen(true);
        }}
        anchorRef={triggerRef}
        placement="bottom"
        align="start"
        offset={4}
        fit={!dropdownWidth}
        onOpenAutoFocus={event => {
          if (!showSearch) {
            event.preventDefault();
            return;
          }
          // 검색형 셀렉트는 열리자마자 검색 입력으로 포커스를 넘겨 기존 사용감과 맞춘다.
          event.preventDefault();
          requestAnimationFrame(() => searchInputRef.current?.focus());
        }}
        onPointerDownOutside={() => {
          dismissedByPointerRef.current = true;
        }}
        onCloseAutoFocus={event => {
          event.preventDefault();
          // Radix FocusScope 는 unmount 포커스 복귀를 setTimeout(0) 으로 미룬다.
          // 다른 셀렉트 트리거를 눌러 닫힌 경우 그 사이 새 셀렉트가 이미 열려 있어서,
          // 여기서 트리거로 포커스를 되돌리면 새로 열린 쪽이 focus-outside 로 보고 즉시 닫힌다.
          if (dismissedByPointerRef.current) return;
          triggerRef.current?.focus();
        }}
        className="rounded-[var(--cmp-listBox-radius)] bg-[var(--cmp-listBox-bg)] shadow-[2px_2px_12px_2px_rgba(0,0,0,0.1)]"
        style={{
          ...(dropdownWidth ? { width: dropdownWidth } : {}),
          maxWidth: maxDropdownWidth,
        }}
      >
        <div
          ref={setListRef}
          className={cn(
            'overflow-auto',
            SCROLLBAR_CLASS,
            SCROLLBAR_TRACK_BG_CLASS,
            SCROLLBAR_TRACK_BORDER_CLASS,
          )}
          style={{ maxHeight: dropdownHeight }}
          onMouseLeave={() => setFocusedIndex(-1)}
        >
          {showSearch && (
            <div
              ref={searchBarRef}
              className="sticky top-0 z-[1] bg-[var(--cmp-listBox-bg)] px-[8px] py-[4px]"
            >
              <div className="flex items-center gap-[6px] rounded-[4px] border border-solid border-[color:var(--color-grey-45)] bg-white px-[8px] py-[4px] focus-within:border-[color:var(--sys-color-field-border-focus)]">
                <SIcon name="search" size={16} color="var(--color-grey-70)" className="shrink-0" />
                <input
                  ref={searchInputRef}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="검색"
                  className="min-w-0 flex-1 border-none bg-transparent text-[12px] leading-[20px] text-[color:var(--cmp-select-text-default)] outline-none placeholder:text-[color:var(--color-grey-55)]"
                />
                {search !== '' && (
                  <SGhostButton
                    icon="close"
                    size="xxs"
                    intent="default"
                    ariaLabel="검색어 지우기"
                    onClick={() => setSearch('')}
                    className="shrink-0"
                  />
                )}
              </div>
            </div>
          )}

          {hasItems ? (
            <ItemList
              items={filtered}
              multi={multi}
              selected={selected}
              groupLeafValues={groupLeafValues}
              focusedValue={navigableItems[focusedIndex]?.key}
              showSelectAll={multi && useSelectAll && allValues.length > 0}
              selectAllLabel={allSelectedLabel}
              selectAllSelected={allSelected}
              onSelectAll={handleSelectAll}
              onSelect={handleSelect}
              onToggleGroup={handleToggleGroup}
              onHoverValue={key => {
                if (key == null) {
                  setFocusedIndex(-1);
                  return;
                }
                const index = navigableItems.findIndex(item => item.key === key);
                if (index >= 0) setFocusedIndex(index);
              }}
            />
          ) : (
            <div className="px-[12px] py-[12px] text-left text-[12px] leading-[20px] text-[color:var(--cmp-listBoxItem-content-disabled)]">
              표시할 항목이 없습니다.
            </div>
          )}
        </div>
      </SPortal>
    </SField>
  );
});

export const SSelect = withDisplayName(SSelectImpl, 'SSelect');

interface ItemListProps {
  items: ReturnType<typeof flattenOptions>;
  multi: boolean;
  selected: string[];
  /** 그룹 렌더 인덱스 → 하위(자손) 리프 value 목록 (multi_depth 부모 체크박스용) */
  groupLeafValues: Map<number, string[]>;
  focusedValue?: string;
  showSelectAll: boolean;
  selectAllSelected: boolean;
  onSelectAll: () => void;
  onSelect: (value: string) => void;
  /** 그룹(부모) 클릭 — 하위 리프 전체 토글 */
  onToggleGroup: (leafValues: string[]) => void;
  /** null 이면 hover 해제 */
  onHoverValue: (value: string | null) => void;
}

function ItemList({
  items,
  multi,
  selected,
  groupLeafValues,
  focusedValue,
  showSelectAll,
  selectAllLabel,
  selectAllSelected,
  onSelectAll,
  onSelect,
  onToggleGroup,
  onHoverValue,
}: ItemListProps & { selectAllLabel: string }) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const itemBase = cn(
    'group/select-item flex w-full cursor-pointer select-none items-center gap-[8px] border-none bg-transparent text-left text-[12px] leading-[20px] outline-none',
    'text-[color:var(--cmp-listBoxItem-content-default)]',
    'disabled:cursor-not-allowed disabled:text-[color:var(--cmp-listBoxItem-content-disabled)]',
  );
  // hover 는 focusedIndex 상태로도 그려지지만 즉각 피드백을 위해 CSS :hover 도 유지.
  // 단 포커스 강조는 focus 가 아닌 focus-visible 로 — multi 에서 옵션 클릭 후 버튼이
  // DOM 포커스를 계속 보유해 파란 배경이 남는(=hover 가 안 풀리는) 문제를 방지한다.
  const activeClass =
    'hover:bg-[var(--cmp-listBoxItem-bg-hover)] hover:text-[color:var(--cmp-listBoxItem-content-hover)] focus-visible:bg-[var(--cmp-listBoxItem-bg-hover)] focus-visible:text-[color:var(--cmp-listBoxItem-content-hover)]';

  return (
    <div role="listbox" aria-multiselectable={multi || undefined}>
      {showSelectAll && (
        <button
          type="button"
          data-select-focused={focusedValue === '__select_all__' ? 'true' : undefined}
          data-select-active={focusedValue === '__select_all__' ? 'true' : undefined}
          className={cn(
            itemBase,
            focusedValue === '__select_all__' &&
              'bg-[var(--cmp-listBoxItem-bg-hover)] text-[color:var(--cmp-listBoxItem-content-hover)]',
          )}
          style={{ paddingTop: 4, paddingBottom: 4, paddingRight: 12, paddingLeft: 12 }}
          onMouseEnter={() => onHoverValue('__select_all__')}
          onMouseLeave={() => onHoverValue(null)}
          onClick={onSelectAll}
        >
          <CheckboxBox state={selectAllSelected} focused={focusedValue === '__select_all__'} />
          <span className="flex-1 truncate">{selectAllLabel}</span>
        </button>
      )}
      {items.map((o, idx) => {
        const padLeft = o.depth === 1 ? 12 : o.depth === 2 ? 20 : 28;
        // value 는 데이터 소스에 따라 드물게 중복될 수 있으므로(예: 분류 코드 중복),
        // React key 는 렌더 순서 인덱스를 조합해 항상 유일하게 만든다.
        const rowKey = `${o.value}-${idx}`;

        if (o.isGroup) {
          const isDepth1 = o.depth === 1;
          // depth1: 상단 구분선 + 파란 배경 + bold, depth2: 회색 배경 + medium (공통)
          const groupTone = isDepth1
            ? 'border-t border-solid border-[color:var(--cmp-listBoxItem-depth1-border-color)] bg-[var(--cmp-listBoxItem-depth1-bg-default)] font-bold'
            : 'bg-[var(--cmp-listBoxItem-depth2-middle-bg)] font-medium';

          // multi 계열: 그룹(부모)도 체크박스 + 하위 리프 전체 토글 (원본 sd-select multi_depth 동작)
          if (multi) {
            const leafValues = groupLeafValues.get(idx) ?? [];
            const total = leafValues.length;
            const selectedCount = leafValues.reduce((n, v) => (selectedSet.has(v) ? n + 1 : n), 0);
            // true=전체선택, null=부분선택(indeterminate), false=미선택
            const groupState: boolean | null =
              total === 0 || selectedCount === 0 ? false : selectedCount === total ? true : null;
            const groupDisabled = o.disabled || total === 0;
            const groupFocused = focusedValue === `__group_${idx}__`;

            return (
              <button
                key={rowKey}
                type="button"
                role="option"
                aria-selected={groupState === true}
                disabled={groupDisabled}
                data-select-focused={groupFocused ? 'true' : undefined}
                data-select-active={groupFocused ? 'true' : undefined}
                className={cn(
                  'flex w-full select-none items-center gap-[8px] border-none py-[4px] pr-[12px] text-left text-[12px] leading-[20px] text-[color:var(--cmp-listBoxItem-content-default)]',
                  groupTone,
                  groupDisabled
                    ? 'cursor-not-allowed text-[color:var(--cmp-listBoxItem-content-disabled)]'
                    : 'cursor-pointer',
                  groupDisabled && isDepth1 && 'bg-[var(--cmp-listBoxItem-depth1-bg-disabled)]',
                  // 포커스 하이라이트는 depth 배경보다 뒤에 둬 tailwind-merge 로 이기게 한다.
                  groupFocused &&
                    !groupDisabled &&
                    'bg-[var(--cmp-listBoxItem-bg-hover)] text-[color:var(--cmp-listBoxItem-content-hover)]',
                )}
                style={{ paddingLeft: padLeft }}
                onMouseEnter={() => onHoverValue(`__group_${idx}__`)}
                onMouseLeave={() => onHoverValue(null)}
                onClick={() => onToggleGroup(leafValues)}
              >
                <CheckboxBox state={groupState} disabled={groupDisabled} focused={groupFocused} />
                {isHtmlLabel(o.label) ? (
                  <span
                    className="min-w-0 truncate"
                    dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(o.label) }}
                  />
                ) : (
                  <span className="min-w-0 truncate">{o.label}</span>
                )}
                {total > 0 && (
                  <span
                    className={cn(
                      'shrink-0 font-medium',
                      groupFocused
                        ? 'text-white'
                        : 'text-[color:var(--cmp-listBoxItem-content-disabled)]',
                    )}
                  >
                    ({selectedCount}/{total})
                  </span>
                )}
              </button>
            );
          }

          // default_depth: 선택 없는 라벨 전용 그룹 헤더
          return (
            <div
              key={rowKey}
              className={cn(
                'select-none py-[4px] pr-[12px] text-[12px] leading-[20px] text-[color:var(--cmp-listBoxItem-content-default)]',
                groupTone,
                // 하위가 전부 비활성이면(select.config 에서 bubble-up) 어미 그룹도 비활성 처리:
                // 텍스트는 회색(#888), depth1 그룹은 파란 배경 대신 회색(#EEE)으로 (depth2 는 #F6F6F6 유지)
                o.disabled &&
                  'cursor-not-allowed text-[color:var(--cmp-listBoxItem-content-disabled)]',
                o.disabled && isDepth1 && 'bg-[var(--cmp-listBoxItem-depth1-bg-disabled)]',
              )}
              style={{ paddingLeft: padLeft }}
            >
              {o.label}
            </div>
          );
        }

        const isSelected = selected.includes(o.value);
        return (
          <button
            key={rowKey}
            type="button"
            disabled={o.disabled}
            role="option"
            aria-selected={isSelected}
            data-select-focused={focusedValue === o.value ? 'true' : undefined}
            data-select-active={focusedValue === o.value ? 'true' : undefined}
            className={cn(
              itemBase,
              isSelected &&
                !multi &&
                'font-bold text-[color:var(--cmp-listBoxItem-content-selected)]',
              focusedValue === o.value &&
                'bg-[var(--cmp-listBoxItem-bg-hover)] text-[color:var(--cmp-listBoxItem-content-hover)]',
              // 비활성 항목은 hover/focus 스타일 대상에서 제외한다.
              // (:hover 는 disabled 버튼에도 매칭되므로 클래스 자체를 붙이지 않아야 한다)
              !o.disabled && activeClass,
            )}
            style={{ paddingTop: 4, paddingBottom: 4, paddingRight: 12, paddingLeft: padLeft }}
            onMouseEnter={() => onHoverValue(o.value)}
            onMouseLeave={() => onHoverValue(null)}
            onClick={() => onSelect(o.value)}
          >
            {multi && (
              <CheckboxBox
                state={isSelected}
                disabled={o.disabled}
                focused={focusedValue === o.value}
              />
            )}
            {isHtmlLabel(o.label) ? (
              <span
                className="flex-1 truncate"
                dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(o.label) }}
              />
            ) : (
              <span className="flex-1 truncate">{o.label}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * 리스트 항목용 체크박스 박스 (전체선택 / 리프 / multi_depth 그룹 공용).
 * state: true=체크, null=부분선택(indeterminate), false=미선택.
 */
function CheckboxBox({
  state,
  disabled = false,
  focused = false,
}: {
  state: boolean | null;
  disabled?: boolean;
  focused?: boolean;
}) {
  // 체크(true)·부분선택(null) 모두 '채워진' 표시(테두리/배경 강조 + 아이콘)
  const filled = state === true || state === null;
  return (
    <span
      className={cn(
        'flex h-[16px] w-[16px] flex-shrink-0 items-center justify-center rounded-[2px] border border-solid',
        disabled
          ? // 비활성: 회색 채움(#E1E1E1) + 회색 테두리(#CCCCCC)
            'cursor-not-allowed border-[color:var(--cmp-checkbox-border-disabled)] bg-[var(--cmp-checkbox-bg-disabled)]'
          : filled
            ? focused
              ? 'border-[color:var(--cmp-checkbox-checked-border-inverse)] bg-[var(--cmp-checkbox-checked-bg-default)]'
              : 'border-[color:var(--cmp-checkbox-checked-bg-default)] bg-[var(--cmp-checkbox-checked-bg-default)]'
            : 'border-[color:var(--cmp-checkbox-unchecked-border-default)] bg-[var(--cmp-checkbox-unchecked-bg-default)]',
      )}
    >
      {filled && (
        <SIcon
          name={state === null ? 'checkboxIndeterminate' : 'checkboxCheck'}
          size={12}
          color={
            disabled
              ? 'var(--cmp-checkbox-checked-icon-disabled)'
              : 'var(--cmp-checkbox-checked-icon-default)'
          }
        />
      )}
    </span>
  );
}
